import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { Box, CircularProgress, Typography, Switch } from "@mui/material";

import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import {
  useLazyListShipmentTransitBranchesQuery,
  useUpdateShipmentTransitBranchMutation,
} from "store/services/shipmentApi";

const ShipmentTransitBranchList = () => {
  const { ShipmentDetail } = useShipmentDetailContext();

  // RTK Query
  const [updateTransitBranch, { isLoading: updatingTransitBranch }] =
    useUpdateShipmentTransitBranchMutation();
  const [fetchTransitBranches, { data: transitBranchesRes, isLoading, isFetching }] =
    useLazyListShipmentTransitBranchesQuery();

  // Handle status toggle
  const handleStatusChange = async (row) => {
    try {
      if (!ShipmentDetail?._id || !row?._id) {
        toast.error("Shipment details or transit branch ID not available");
        return;
      }

      const newStatus = !row.isActive;

      await updateTransitBranch({
        transitBranchId: row._id,
        shipmentId: ShipmentDetail._id,
        isActive: newStatus,
      }).unwrap();

      toast.success(
        `Transit branch ${newStatus ? "activated" : "deactivated"} successfully!`
      );

      // Refresh the transit branches list
      fetchTransitBranches({ shipmentId: ShipmentDetail._id });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update transit branch status!");
    }
  };

  const columns = [
    {
      name: "Branch Name",
      selector: (row) => row?.transitBranch?.name || "",
      cell: (row) => row?.transitBranch?.name || "N/A",
      sortable: true,
    },
    {
      name: "Branch Code",
      selector: (row) => row?.transitBranch?.code || "",
      cell: (row) => row?.transitBranch?.code || "N/A",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row?.isActive,
      cell: (row) => (
        <Typography variant="body2" color={row.isActive ? "success.main" : "error.main"}>
          {row.isActive ? "Active" : "Inactive"}
        </Typography>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={row.isActive === true}
            onChange={() => handleStatusChange(row)}
            color="primary"
            size="small"
            inputProps={{ "aria-label": "Status Toggle" }}
            disabled={ShipmentDetail?.isActive === false || updatingTransitBranch}
          />
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useEffect(() => {
    if (!ShipmentDetail?._id) {
      return;
    }
    fetchTransitBranches({
      shipmentId: ShipmentDetail._id,
    });
  }, [ShipmentDetail?._id, fetchTransitBranches]);

  return (
    <div>
      <BreadcrumbLayout.Paper>
        {transitBranchesRes ? (
          <AppTable
            columns={columns}
            data={Array.isArray(transitBranchesRes?.data) ? transitBranchesRes.data : []}
            progressPending={isLoading || isFetching || updatingTransitBranch}
            progressComponent={
              <Box display="flex" alignItems="center" padding={2}>
                <CircularProgress size={24} />
                <Typography variant="body2" marginLeft={1}>
                  Loading Transit Branches...
                </Typography>
              </Box>
            }
            noDataComponent={
              <Typography variant="body2" padding={2}>
                No transit branches found.
              </Typography>
            }
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>
              Loading Transit Branches...
            </Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
    </div>
  );
};

export default ShipmentTransitBranchList;